import { ClinicalSummaryCard } from '@/components/order/ClinicalSummaryCard'
import { FamilyCard, FamilyHistoryCard } from '@/components/order/FamilyCard'
import { IndicationsCard } from '@/components/order/IndicationsCard'
import { phenotypeLabelsFromCanonicalHpoWithOverrides } from '@/components/order/parentPhenotypeLabels'
import { TestOptionsCard } from '@/components/order/TestOptionsCard'
import {
  formatTurnaroundTimeSequencingCardSubtitle,
  getTestTypeConfig,
  getTurnaroundTimeDayBasisForTestType,
} from '@/config/uiConfig'
import { useUiConfig } from '@/config/uiConfigHooks'
import { extractHPOId, isHpoCode } from '@/features/terminology/hpo/hpoTypes'
import { hpoAPIClient } from '@/features/terminology/hpo/HPOAPIClient'
import { orderTypeFromComparators, parentComparatorCountForOrder } from '@/ordering/comparatorReadAdapter'
import { useAppSelector } from '@/store/hooks'
import {
  CollectionType,
  normalizePhenotypicTrait,
  phenotypicTraitShowsAffectedPresentation,
  TestType,
} from '@/types/RequisitionFormTypes'
import { FormCompletionChecker } from '@/utils/FormCompletionChecker'
import {
  mapCategoryToType,
  mapCollectionStringToSampleTypeStrict,
  mapCollectionTypeToSampleType,
} from '@/utils/orderFormUtils'
import * as Sentry from '@sentry/react'
import { useEffect, useMemo, useState } from 'react'

/**
 * Read-only "Order details" summary (test options, clinical summary, indications
 * and family cards) derived entirely from the canonical order in Redux plus the
 * active UI config. Shared by the Results and Guidance pages so both surfaces
 * present an identical, single-sourced order recap.
 */
export function OrderDetailsPanel() {
  const { config } = useUiConfig()
  const { canonicalOrder: storedCanonical } = useAppSelector((s) => s.canonicalOrder)

  const selectedTestTypeConfig = useMemo(
    () => getTestTypeConfig((storedCanonical?.order?.selectedOption ?? '') as TestType, config),
    [config, storedCanonical?.order?.selectedOption]
  )
  const selectedTestTypeLabel =
    selectedTestTypeConfig?.label ??
    selectedTestTypeConfig?.category ??
    config.testTypes[0]?.category ??
    'Ultra-Rapid Whole Genome Sequencing (urWGS)'
  const turnaroundDays = selectedTestTypeConfig?.turnaroundTimeInDays ?? 5
  const turnaroundSequencingSubtitle = formatTurnaroundTimeSequencingCardSubtitle(
    turnaroundDays,
    getTurnaroundTimeDayBasisForTestType(selectedTestTypeConfig)
  )
  // Decoupling (R5): a placed duo/trio order can run on a category SKU whose
  // config tier is 0, so the family structure must come from the order's
  // comparators / orderType, not the test type's parentSamplesRequired.
  const familySamplesCount = parentComparatorCountForOrder(storedCanonical)
  const comparatorSamplesAllowed = familySamplesCount > 0
  const familySamplesTitle = comparatorSamplesAllowed
    ? `Include ${familySamplesCount} family sample${familySamplesCount !== 1 ? 's' : ''} for comparison`
    : 'No comparator samples'
  // Name the actual parent comparator(s) on the order rather than a generic
  // literal, mirroring the Order Start summary (`familySamplesDisplay`). Parent
  // presence is derived from the comparator set (falling back to `orderType`
  // for legacy orders) via `orderTypeFromComparators`.
  const familySamplesDescription = useMemo(() => {
    if (!comparatorSamplesAllowed) return 'Family members won’t be tested'
    const tier = orderTypeFromComparators(storedCanonical)
    const orderTypeFallback = (storedCanonical?.orderType ?? '').trim()
    const hasMother = tier === 'FAMILY_TRIO' || tier === 'FAMILY_DUO_MOTHER' || orderTypeFallback === 'FAMILY_DUO_MOTHER'
    const hasFather = tier === 'FAMILY_TRIO' || tier === 'FAMILY_DUO_FATHER' || orderTypeFallback === 'FAMILY_DUO_FATHER'
    const members: string[] = []
    if (hasMother) members.push('Biological Mother')
    if (hasFather) members.push('Biological Father')
    return members.length > 0 ? members.join(', ') : 'Biological parent'
  }, [comparatorSamplesAllowed, storedCanonical])

  /**
   * These pages are read-only, but legacy canonical orders can persist HPO
   * `label` fields that are the HP code itself (e.g. "HP:0001250"). Resolve them
   * on the client into human-readable text via the HPO terminology service and
   * substitute them at render time. Keyed by HPO id; covers both Indications for
   * Testing and parent-comparator phenotype groups in one pass to avoid N+1
   * fetch cycles.
   */
  const [hpoLabelOverrides, setHpoLabelOverrides] = useState<Record<string, string>>({})

  const clinicalDiagnosis = storedCanonical?.reason?.clinicalDiagnosis
  const indicationsGroups = useMemo(() => {
    const terms = Array.isArray(clinicalDiagnosis) ? clinicalDiagnosis : []
    const phenotypes = terms.map((term) => {
      const id = String(term.id ?? '').trim()
      const rawLabel = (term.label ?? '').trim()
      const override = id ? hpoLabelOverrides[id]?.trim() : undefined
      return {
        id,
        name: override || rawLabel,
        type: mapCategoryToType(term.category),
      }
    })
    const getItems = (type: 'key' | 'additional' | 'excluded') =>
      phenotypes.filter((t) => t.type === type).map((t) => t.name)
    return [
      { title: 'Key Phenotypes', items: getItems('key') },
      { title: 'Additional Phenotypes', items: getItems('additional') },
      { title: 'Pertinent Negatives', items: getItems('excluded'), isEmpty: getItems('excluded').length === 0 },
    ]
  }, [clinicalDiagnosis, hpoLabelOverrides])

  // C1.4: comparator-shaped views for parent display. Mirrors the
  // C1.1 / C1.3 pattern so cards render consent / specimen / clinical
  // metadata that lives on the comparator (but not the legacy slot) for
  // old orders too.
  const motherComp = useMemo(() => FormCompletionChecker.readParent(storedCanonical, 'mother'), [storedCanonical])
  const fatherComp = useMemo(() => FormCompletionChecker.readParent(storedCanonical, 'father'), [storedCanonical])

  const showMotherCard = Boolean(motherComp?.demographic?.firstName || motherComp?.demographic?.lastName)
  const showFatherCard = Boolean(fatherComp?.demographic?.firstName || fatherComp?.demographic?.lastName)

  useEffect(() => {
    const collectPending = (terms: unknown): string[] => {
      if (!Array.isArray(terms)) return []
      const ids: string[] = []
      for (const raw of terms) {
        const t = raw as { id?: string; label?: string }
        const id = String(t?.id ?? '').trim()
        const label = String(t?.label ?? '').trim()
        if (id && isHpoCode(label) && !(id in hpoLabelOverrides)) {
          ids.push(id)
        }
      }
      return ids
    }
    const pendingIds = Array.from(
      new Set([
        ...collectPending(clinicalDiagnosis as unknown),
        ...collectPending(motherComp?.hpoTerms as unknown),
        ...collectPending(fatherComp?.hpoTerms as unknown),
      ])
    )
    if (pendingIds.length === 0) return

    let cancelled = false
    const resolve = async () => {
      const additions: Record<string, string> = {}
      for (const rawId of pendingIds) {
        const normalized = extractHPOId(rawId)
        try {
          const node = await hpoAPIClient.getHPOTermById(normalized)
          if (node?.lbl && !isHpoCode(node.lbl)) {
            additions[rawId] = node.lbl
          }
        } catch (error) {
          Sentry.captureException(error, { tags: { operation: 'order.details.hpo-label.resolve' } })
        }
      }
      if (cancelled || Object.keys(additions).length === 0) return
      setHpoLabelOverrides((prev) => ({ ...prev, ...additions }))
    }
    void resolve()
    return () => {
      cancelled = true
    }
    // `hpoLabelOverrides` is intentionally omitted; the `pendingIds` guard
    // already prevents re-runs once an id has been resolved.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clinicalDiagnosis, motherComp?.hpoTerms, fatherComp?.hpoTerms])

  return (
    <div className="flex flex-col gap-4">
      <h2 className="text-lg font-semibold tracking-tight text-text-heading dark:text-white">Order details</h2>
      <TestOptionsCard
        testTypeLabel={selectedTestTypeLabel}
        turnaroundDescription={turnaroundSequencingSubtitle}
        familySamples={{ title: familySamplesTitle, description: familySamplesDescription }}
        sampleType={mapCollectionTypeToSampleType(storedCanonical?.patient?.collectionType as CollectionType | undefined)}
        statusSuccessText="Sent to lab"
      />

      <ClinicalSummaryCard
        summary={storedCanonical?.clinical?.clinicalNote ?? ''}
        differentialDiagnosis={storedCanonical?.clinical?.differentialDiagnosis ?? ''}
        priorGeneticTesting={storedCanonical?.clinical?.priorGeneticTesting ?? ''}
        emptyStateText="None entered"
        statusMessage="Sent to lab"
        statusVariant="success"
      />

      <IndicationsCard
        groups={indicationsGroups}
        statusMessage="Sent to lab"
        statusVariant="success"
        onEdit={undefined}
      />

      <FamilyHistoryCard
        hasData={Boolean(storedCanonical?.family?.familyHistory)}
        summary={storedCanonical?.family?.familyHistory || ''}
        statusMessage="Sent to lab"
        statusVariant="success"
        showEmptyState={true}
      />

      {(showMotherCard || showFatherCard) && (
        <>
          <FamilyCard
            title="Biological Mother"
            hasData={showMotherCard}
            contactInfo={
              showMotherCard
                ? {
                    name: `${motherComp?.demographic?.firstName || ''} ${motherComp?.demographic?.lastName || ''}`.trim(),
                    age: motherComp?.demographic?.dateOfBirth ?? undefined,
                    email: motherComp?.demographic?.email ?? undefined,
                    phone: motherComp?.demographic?.phone ?? undefined,
                    medicalRecordNumber: motherComp?.demographic?.mrn ?? undefined,
                    sampleType:
                      mapCollectionStringToSampleTypeStrict(motherComp?.specimen?.collectionType ?? null) ??
                      undefined,
                  }
                : undefined
            }
            phenotypeLabels={
              showMotherCard
                ? phenotypeLabelsFromCanonicalHpoWithOverrides(motherComp?.hpoTerms, hpoLabelOverrides)
                : undefined
            }
            clinicalNotes={
              phenotypicTraitShowsAffectedPresentation(
                normalizePhenotypicTrait(motherComp?.phenotypicTrait)
              )
                ? motherComp?.clinicalNote?.trim() || undefined
                : undefined
            }
            showPrimaryGuardianBadge={showMotherCard && motherComp?.isPrimaryGuardian === true}
            statusMessage={showMotherCard ? 'Sent to lab' : 'Not included'}
            statusVariant={showMotherCard ? 'success' : 'info'}
          />

          <FamilyCard
            title="Biological Father"
            hasData={showFatherCard}
            contactInfo={
              showFatherCard
                ? {
                    name: `${fatherComp?.demographic?.firstName || ''} ${fatherComp?.demographic?.lastName || ''}`.trim(),
                    age: fatherComp?.demographic?.dateOfBirth ?? undefined,
                    email: fatherComp?.demographic?.email ?? undefined,
                    phone: fatherComp?.demographic?.phone ?? undefined,
                    medicalRecordNumber: fatherComp?.demographic?.mrn ?? undefined,
                    sampleType:
                      mapCollectionStringToSampleTypeStrict(fatherComp?.specimen?.collectionType ?? null) ??
                      undefined,
                  }
                : undefined
            }
            phenotypeLabels={
              showFatherCard
                ? phenotypeLabelsFromCanonicalHpoWithOverrides(fatherComp?.hpoTerms, hpoLabelOverrides)
                : undefined
            }
            clinicalNotes={
              phenotypicTraitShowsAffectedPresentation(
                normalizePhenotypicTrait(fatherComp?.phenotypicTrait)
              )
                ? fatherComp?.clinicalNote?.trim() || undefined
                : undefined
            }
            showPrimaryGuardianBadge={showFatherCard && fatherComp?.isPrimaryGuardian === true}
            statusMessage={showFatherCard ? 'Sent to lab' : 'Not included'}
            statusVariant={showFatherCard ? 'success' : 'info'}
          />
        </>
      )}
    </div>
  )
}

export default OrderDetailsPanel
